import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";

import { meKey } from "@/app/session/me";
import { api, unwrap, type Schemas } from "@/shared/api/client";
import { unwrapEmpty } from "@/shared/api/empty";
import { unwrapPage, type PageOf } from "@/shared/api/page";
import { makeKeys } from "@/shared/api/queryKeys";

export type Member = Schemas["MemberOut"];
export type MemberUpdate = Schemas["MemberUpdate"];
export type MemberInvite = Schemas["MemberInvite"];
export type Department = Schemas["DepartmentOut"];
export type AuditEntry = Schemas["AuditEntryOut"];
export type RolePermissions = Schemas["RolePermissionsOut"];
export type StaffRole = Schemas["StaffRole"];
export type MemberPermissionsUpdate = Schemas["MemberPermissionsUpdate"];

export const memberKeys = makeKeys("team-members");
export const departmentKeys = makeKeys("departments");
export const roleKeys = makeKeys("roles");
export const permissionCatalogKeys = makeKeys("permission-catalog");
export const auditKeys = makeKeys("audit");

const PAGE_SIZE = 100;
const AUDIT_PAGE_SIZE = 50;
/* What a role may do changes only with a release. */
const ROLES_STALE_MS = 10 * 60_000;
/* Member edits run one at a time, so each one sends the version the previous one returned. */
const MEMBER_EDITS = { id: "team-member-edits" };

type MemberPages = InfiniteData<PageOf<Member>, string | null>;

/* The whole team in one list (status=all): the screen filters and counts it in place. */
const MEMBERS_KEY = memberKeys.list({ status: "all" });

export function useMembers() {
  return useInfiniteQuery({
    queryKey: MEMBERS_KEY,
    queryFn: ({ pageParam }) =>
      unwrapPage(
        api.GET("/api/v1/team/members", {
          params: { query: { status: "all", limit: PAGE_SIZE, cursor: pageParam } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });
}

function cachedMembers(queryClient: QueryClient): Member[] {
  const pages = queryClient.getQueryData<MemberPages>(MEMBERS_KEY)?.pages ?? [];
  return pages.flatMap((page) => page.items);
}

function mapMembers(queryClient: QueryClient, change: (member: Member) => Member | null): void {
  queryClient.setQueryData<MemberPages>(MEMBERS_KEY, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.flatMap((member) => {
              const next = change(member);
              return next ? [next] : [];
            }),
          })),
        }
      : data,
  );
}

/** Put the server's copy of a member into the list. */
function storeMember(queryClient: QueryClient, saved: Member): void {
  mapMembers(queryClient, (member) => (member.id === saved.id ? saved : member));
}

/** The version to send with If-Match: the latest the list knows, read when the request leaves. */
function versionOf(queryClient: QueryClient, memberId: string): string {
  const member = cachedMembers(queryClient).find((candidate) => candidate.id === memberId);
  return String(member?.version ?? 0);
}

/** Departments' member counts and the activity log follow every change to the team. */
function refreshTeam(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: departmentKeys.all }),
    queryClient.invalidateQueries({ queryKey: auditKeys.all }),
  ]);
}

export interface MemberEdit {
  memberId: string;
  changes: MemberUpdate;
  /** How the member looks once saved, shown right away and undone if the save fails. */
  preview: Partial<Member>;
}

export function useUpdateMember() {
  const queryClient = useQueryClient();
  return useMutation({
    scope: MEMBER_EDITS,
    mutationFn: ({ memberId, changes }: MemberEdit) =>
      unwrap(
        api.PATCH("/api/v1/team/members/{member_id}", {
          params: {
            path: { member_id: memberId },
            header: { "if-match": versionOf(queryClient, memberId) },
          },
          body: changes,
        }),
      ),
    onMutate: async ({ memberId, preview }) => {
      await queryClient.cancelQueries({ queryKey: MEMBERS_KEY });
      const previous = queryClient.getQueryData<MemberPages>(MEMBERS_KEY);
      mapMembers(queryClient, (member) =>
        member.id === memberId ? { ...member, ...preview } : member,
      );
      return { previous };
    },
    onError: (_error, _edit, context) => {
      if (context?.previous) queryClient.setQueryData(MEMBERS_KEY, context.previous);
    },
    onSuccess: (saved) => {
      storeMember(queryClient, saved);
      return Promise.all([
        refreshTeam(queryClient),
        // Someone may have changed their own department or title.
        queryClient.invalidateQueries({ queryKey: meKey }),
      ]);
    },
  });
}

export interface MemberPermissionsEdit {
  memberId: string;
  overrides: MemberPermissionsUpdate;
  /** How the member looks once saved (their keys and overrides), undone if the save fails. */
  preview: Pick<Member, "granted_permissions" | "revoked_permissions" | "permissions">;
}

/** Replace one person's permission overrides; empty lists put them back on their role. */
export function useSetMemberPermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    scope: MEMBER_EDITS,
    mutationFn: ({ memberId, overrides }: MemberPermissionsEdit) =>
      unwrap(
        api.PUT("/api/v1/team/members/{member_id}/permissions", {
          params: {
            path: { member_id: memberId },
            header: { "if-match": versionOf(queryClient, memberId) },
          },
          body: overrides,
        }),
      ),
    onMutate: async ({ memberId, preview }) => {
      await queryClient.cancelQueries({ queryKey: MEMBERS_KEY });
      const previous = queryClient.getQueryData<MemberPages>(MEMBERS_KEY);
      mapMembers(queryClient, (member) =>
        member.id === memberId ? { ...member, ...preview } : member,
      );
      return { previous };
    },
    onError: (_error, _edit, context) => {
      if (context?.previous) queryClient.setQueryData(MEMBERS_KEY, context.previous);
    },
    onSuccess: (saved) => {
      storeMember(queryClient, saved);
      return refreshTeam(queryClient);
    },
  });
}

type MemberStatusAction = "deactivate" | "reactivate";

/** Deactivate (sessions end at once) or reactivate a member. */
export function useSetMemberActive(action: MemberStatusAction) {
  const queryClient = useQueryClient();
  return useMutation({
    scope: MEMBER_EDITS,
    mutationFn: (memberId: string) => {
      const options = {
        params: {
          path: { member_id: memberId },
          header: { "if-match": versionOf(queryClient, memberId) },
        },
      };
      return unwrap(
        action === "deactivate"
          ? api.POST("/api/v1/team/members/{member_id}/deactivate", options)
          : api.POST("/api/v1/team/members/{member_id}/reactivate", options),
      );
    },
    onSuccess: (saved) => {
      storeMember(queryClient, saved);
      return refreshTeam(queryClient);
    },
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MemberInvite) => unwrap(api.POST("/api/v1/team/invitations", { body })),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: memberKeys.all }),
        refreshTeam(queryClient),
      ]),
  });
}

/** A fresh link for a pending invitation; the old link stops working. */
export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(
        api.POST("/api/v1/team/invitations/{invitation_id}/resend", {
          params: { path: { invitation_id: invitationId } },
        }),
      ),
    onSuccess: ({ member }) => {
      storeMember(queryClient, member);
      return queryClient.invalidateQueries({ queryKey: auditKeys.all });
    },
  });
}

/** Withdraw an invitation: the person who never joined leaves the list. */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invitationId }: { invitationId: string; memberId: string }) =>
      unwrapEmpty(
        api.DELETE("/api/v1/team/invitations/{invitation_id}", {
          params: { path: { invitation_id: invitationId } },
        }),
      ),
    onSuccess: (_result, { memberId }) => {
      mapMembers(queryClient, (member) => (member.id === memberId ? null : member));
      return refreshTeam(queryClient);
    },
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: departmentKeys.list(),
    queryFn: () => unwrap(api.GET("/api/v1/team/departments")),
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => unwrap(api.POST("/api/v1/team/departments", { body: { name } })),
    onSuccess: (created) => {
      queryClient.setQueryData<Department[]>(departmentKeys.list(), (list) =>
        list ? [...list, created] : list,
      );
      return refreshTeam(queryClient);
    },
  });
}

function setDepartments(
  queryClient: QueryClient,
  change: (list: Department[]) => Department[],
): Department[] | undefined {
  const previous = queryClient.getQueryData<Department[]>(departmentKeys.list());
  if (previous) queryClient.setQueryData(departmentKeys.list(), change(previous));
  return previous;
}

export function useRenameDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ department, name }: { department: Department; name: string }) =>
      unwrap(
        api.PATCH("/api/v1/team/departments/{department_id}", {
          params: {
            path: { department_id: department.id },
            header: { "if-match": String(department.version) },
          },
          body: { name },
        }),
      ),
    onMutate: async ({ department, name }) => {
      await queryClient.cancelQueries({ queryKey: departmentKeys.all });
      const previous = setDepartments(queryClient, (list) =>
        list.map((item) => (item.id === department.id ? { ...item, name } : item)),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(departmentKeys.list(), context.previous);
    },
    onSettled: () =>
      Promise.all([
        refreshTeam(queryClient),
        // Members show their department's name.
        queryClient.invalidateQueries({ queryKey: memberKeys.all }),
      ]),
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (departmentId: string) =>
      unwrapEmpty(
        api.DELETE("/api/v1/team/departments/{department_id}", {
          params: { path: { department_id: departmentId } },
        }),
      ),
    onMutate: async (departmentId) => {
      await queryClient.cancelQueries({ queryKey: departmentKeys.all });
      const previous = setDepartments(queryClient, (list) =>
        list.filter((item) => item.id !== departmentId),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(departmentKeys.list(), context.previous);
    },
    onSettled: () => refreshTeam(queryClient),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: roleKeys.list(),
    queryFn: () => unwrap(api.GET("/api/v1/team/roles")),
    staleTime: ROLES_STALE_MS,
  });
}

/** Every key, the key it brings along and whether one person's set may change it. */
export function usePermissionCatalog() {
  return useQuery({
    queryKey: permissionCatalogKeys.list(),
    queryFn: () => unwrap(api.GET("/api/v1/team/permissions")),
    staleTime: ROLES_STALE_MS,
  });
}

export function useAudit() {
  return useInfiniteQuery({
    queryKey: auditKeys.list(),
    queryFn: ({ pageParam }) =>
      unwrapPage(
        api.GET("/api/v1/team/audit", {
          params: { query: { limit: AUDIT_PAGE_SIZE, cursor: pageParam } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });
}
